import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import AddWorkflowRunSource from '@Application/WorkflowRuns/Sources/Commands/AddWorkflowRunSource';
import addWorkflowRunSource from '@Application/WorkflowRuns/Sources/addWorkflowRunSource';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import type ElementDescription from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Shared/Helpers/ElementDescription';
import { closeAddFromDossierSheet } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/Signals/addFromDossierSheetSignal';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import { css } from '@emotion/react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import ElementTile from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Shared/ElementTile';
import type { ElementTileKind } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Shared/ElementTile';
import { useDispatch } from '@verclam/synapse';
import { Button } from '@verclam/ui-kit';

const rowStyle = (isUsed: boolean) => css`
	display: flex;
	gap: 0.75rem;
	align-items: center;
	flex-wrap: wrap;
	padding: 0.875rem 1rem;
	border-radius: ${theme.radii.medium};
	background-color: ${theme.colors.surfaceMuted};
	opacity: ${isUsed ? 0.62 : 1};
`;

const textStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.125rem;
	flex: 1;
	min-width: 10rem;
`;

const titleStyle = css`
	font-family: ${theme.fonts.body};
	font-size: 0.875rem;
	font-weight: ${theme.fontWeights.semibold};
	line-height: 1.35;
	color: ${theme.colors.ink};
`;

const subtitleStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: 0.78125rem;
	line-height: 1.55;
	color: ${theme.colors.inkMuted};
`;

const linkStyle = css`
	font-family: ${theme.fonts.body};
	font-size: 0.78125rem;
	font-weight: ${theme.fontWeights.semibold};
	color: ${theme.colors.accent};
`;

type Props = {
	readonly kind: ElementTileKind;
	/** State chip of a document (« Vérifiée », « À relire »). */
	readonly chip?: React.ReactNode;
	/** The preparation already holds this candidate: shown, but not pickable. */
	readonly isUsed: boolean;
	readonly subject: SubjectAddress;
	readonly preparationId: number;
	readonly description: ElementDescription;
	/** Existing detail screen of the candidate (« Voir »). */
	readonly viewPath: string | null;
	/** Why the candidate cannot be added (failed conversion): shown instead of the « Ajouter » button. */
	readonly unavailableReason?: string | null;
	readonly interviewId: number | null;
	readonly documentId: number | null;
	readonly importedDocumentId: number | null;
	/** Set when the document replaces the one carried over for this type. */
	readonly replacesDocumentTypeId: number | null;
};

/**
 * One candidate of the « Ajouter depuis le dossier » sheet: an interview, a note, a document or an imported document of the
 * dossier, with a « Voir » link to its detail and its own « Ajouter » (leaf action: it dispatches). The
 * sheet closes after a replacement — a single document is chosen — and stays open otherwise.
 */
const AddCandidateRow: React.FC<Props> = ({
	kind,
	chip,
	isUsed,
	subject,
	preparationId,
	description,
	viewPath,
	unavailableReason = null,
	interviewId,
	documentId,
	importedDocumentId,
	replacesDocumentTypeId,
}) => {
	const dispatch = useDispatch();
	const { currentFacility } = useStrictCurrentFacility();
	const facilityId = currentFacility.id;
	const [isAdding, setIsAdding] = React.useState(false);

	const handleAddClick = React.useCallback(() => {
		setIsAdding(true);
		dispatch(
			addWorkflowRunSource(
				new AddWorkflowRunSource(
					facilityId,
					subject,
					preparationId,
					interviewId,
					documentId,
					importedDocumentId,
					replacesDocumentTypeId,
				),
			),
		)
			.then(() => {
				if (replacesDocumentTypeId !== null) {
					closeAddFromDossierSheet();
				}
			})
			.catch(ignoreHandledError)
			.finally(() => {
				setIsAdding(false);
			});
	}, [
		dispatch,
		facilityId,
		subject,
		preparationId,
		interviewId,
		documentId,
		importedDocumentId,
		replacesDocumentTypeId,
	]);

	return (
		<li css={rowStyle(isUsed)}>
			<ElementTile kind={kind} size="2.25rem" />
			<div css={textStyle}>
				<span css={titleStyle}>{description.title}</span>
				<p css={subtitleStyle}>{description.subtitle}</p>
				<ConditionalLoader data={unavailableReason}>
					{(reason) => <p css={subtitleStyle}>{reason}</p>}
				</ConditionalLoader>
			</div>
			{chip}
			<ConditionalLoader data={viewPath}>
				{(path) => (
					<Link css={linkStyle} to={path} aria-label={`Voir « ${description.title} »`}>
						Voir
					</Link>
				)}
			</ConditionalLoader>
			<ConditionalLoader data={unavailableReason === null}>
				<Button
					type="button"
					size="sm"
					label={
						isUsed
							? 'Déjà dans la préparation'
							: replacesDocumentTypeId !== null
								? 'Choisir'
								: 'Ajouter'
					}
					disabled={isUsed}
					loading={isAdding}
					onClick={handleAddClick}
				/>
			</ConditionalLoader>
		</li>
	);
};

export default AddCandidateRow;
